import {
  formatShortDate,
  getTransactionAmount,
  type TransactionRow,
} from './types';

export interface PeriodStats {
  credits: number;
  debits: number;
  net: number;
  count: number;
}

export interface TimeSeriesPoint {
  key: string;
  label: string;
  credits: number;
  debits: number;
  net: number;
}

export interface TypeShareItem {
  name: string;
  value: number;
  fill: string;
  [key: string]: string | number;
}

export interface CounterpartyItem {
  name: string;
  total: number;
  count: number;
  direction: 'Credit' | 'Debit' | 'Mixed';
}

export interface InsightChip {
  id: string;
  label: string;
  detail: string;
  tone: 'info' | 'warning' | 'neutral';
}

export interface ExtendedKpis {
  avgSize: number;
  largestCredit: number;
  largestDebit: number;
  feeTotal: number;
  mostActiveDay: string | null;
  dailyNetSparkline: number[];
}

export type DatePreset = '7d' | '30d' | '90d' | 'this_month' | 'last_month' | 'all';

const DONUT_COLORS = ['#10b981', '#f59e0b', '#6366f1', '#ec4899', '#14b8a6', '#f97316', '#8b5cf6'];

function parseTxDate(iso?: string | null): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function endOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(23, 59, 59, 999);
  return copy;
}

function parseDateInput(value: string): Date | null {
  const parts = value.split('-').map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) return null;
  const [year, month, day] = parts;
  return new Date(year, month - 1, day);
}

export function getDatePresetRange(preset: DatePreset, reference = new Date()): { from: Date | null; to: Date | null } {
  if (preset === 'all') return { from: null, to: null };

  const to = endOfDay(reference);

  if (preset === '7d') {
    const from = startOfDay(new Date(reference));
    from.setDate(from.getDate() - 6);
    return { from, to };
  }
  if (preset === '30d') {
    const from = startOfDay(new Date(reference));
    from.setDate(from.getDate() - 29);
    return { from, to };
  }
  if (preset === '90d') {
    const from = startOfDay(new Date(reference));
    from.setDate(from.getDate() - 89);
    return { from, to };
  }
  if (preset === 'this_month') {
    const from = startOfDay(new Date(reference.getFullYear(), reference.getMonth(), 1));
    return { from, to };
  }
  if (preset === 'last_month') {
    const from = startOfDay(new Date(reference.getFullYear(), reference.getMonth() - 1, 1));
    const toLast = endOfDay(new Date(reference.getFullYear(), reference.getMonth(), 0));
    return { from, to: toLast };
  }

  return { from: null, to: null };
}

export function filterTransactions(
  transactions: TransactionRow[],
  options: {
    direction?: 'all' | 'Credit' | 'Debit';
    types?: string[];
    dateFrom?: string;
    dateTo?: string;
    dateFromDate?: Date | null;
    dateToDate?: Date | null;
    search?: string;
    amountMin?: number | null;
    amountMax?: number | null;
  } = {}
): TransactionRow[] {
  const search = options.search?.trim().toLowerCase() || '';

  return transactions.filter((tx) => {
    if (options.direction && options.direction !== 'all' && tx.direction !== options.direction) {
      return false;
    }
    if (options.types && options.types.length > 0 && (!tx.type || !options.types.includes(tx.type))) {
      return false;
    }

    const amount = getTransactionAmount(tx);
    if (options.amountMin != null && amount < options.amountMin) return false;
    if (options.amountMax != null && amount > options.amountMax) return false;

    if (search) {
      const haystack = [tx.summary, tx.name, tx.type, tx.direction]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }

    const txDate = parseTxDate(tx.createdAt);
    if (!txDate) {
      return !options.dateFrom && !options.dateTo && !options.dateFromDate && !options.dateToDate;
    }

    if (options.dateFromDate) {
      if (txDate < options.dateFromDate) return false;
    } else if (options.dateFrom) {
      const parsedFrom = parseDateInput(options.dateFrom);
      if (!parsedFrom) return false;
      const from = startOfDay(parsedFrom);
      if (txDate < from) return false;
    }

    if (options.dateToDate) {
      if (txDate > options.dateToDate) return false;
    } else if (options.dateTo) {
      const parsedTo = parseDateInput(options.dateTo);
      if (!parsedTo) return false;
      const to = endOfDay(parsedTo);
      if (txDate > to) return false;
    }

    return true;
  });
}

export function computePeriodStats(transactions: TransactionRow[], from: Date, to: Date): PeriodStats {
  let credits = 0;
  let debits = 0;
  let count = 0;

  for (const tx of transactions) {
    const date = parseTxDate(tx.createdAt);
    if (!date || date < from || date > to) continue;

    const amount = getTransactionAmount(tx);
    count += 1;
    if (tx.direction === 'Credit') credits += amount;
    else if (tx.direction === 'Debit') debits += amount;
  }

  return {
    credits: Math.round(credits * 100) / 100,
    debits: Math.round(debits * 100) / 100,
    net: Math.round((credits - debits) * 100) / 100,
    count,
  };
}

export function getCurrentAndPreviousMonthStats(transactions: TransactionRow[], reference = new Date()) {
  const thisMonthFrom = startOfDay(new Date(reference.getFullYear(), reference.getMonth(), 1));
  const thisMonthTo = endOfDay(reference);
  const lastMonthFrom = startOfDay(new Date(reference.getFullYear(), reference.getMonth() - 1, 1));
  const lastMonthTo = endOfDay(new Date(reference.getFullYear(), reference.getMonth(), 0));

  return {
    current: computePeriodStats(transactions, thisMonthFrom, thisMonthTo),
    previous: computePeriodStats(transactions, lastMonthFrom, lastMonthTo),
    currentLabel: reference.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
    previousLabel: lastMonthFrom.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
  };
}

export function buildTimeSeries(transactions: TransactionRow[]): TimeSeriesPoint[] {
  const buckets = new Map<string, TimeSeriesPoint>();

  for (const tx of transactions) {
    const date = parseTxDate(tx.createdAt);
    if (!date) continue;

    const key = date.toISOString().slice(0, 10);
    const label = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const existing = buckets.get(key) || { key, label, credits: 0, debits: 0, net: 0 };
    const amount = getTransactionAmount(tx);

    if (tx.direction === 'Credit') existing.credits += amount;
    else if (tx.direction === 'Debit') existing.debits += amount;
    existing.net = existing.credits - existing.debits;
    buckets.set(key, existing);
  }

  return Array.from(buckets.values())
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((point) => ({
      ...point,
      credits: Math.round(point.credits * 100) / 100,
      debits: Math.round(point.debits * 100) / 100,
      net: Math.round(point.net * 100) / 100,
    }));
}

export function buildTypeDonutData(transactions: TransactionRow[]): TypeShareItem[] {
  const totals = new Map<string, number>();

  for (const tx of transactions) {
    const type = tx.type || 'Unknown';
    totals.set(type, (totals.get(type) || 0) + getTransactionAmount(tx));
  }

  return Array.from(totals.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name, value], index) => ({
      name,
      value: Math.round(value * 100) / 100,
      fill: DONUT_COLORS[index % DONUT_COLORS.length],
    }));
}

export function getTopCounterparties(transactions: TransactionRow[], limit = 5): CounterpartyItem[] {
  const map = new Map<string, { total: number; count: number; credits: number; debits: number }>();

  for (const tx of transactions) {
    const label = (tx.name || tx.summary?.split('|')[0]?.trim() || 'Unknown').trim();
    const existing = map.get(label) || { total: 0, count: 0, credits: 0, debits: 0 };
    const amount = getTransactionAmount(tx);
    existing.total += amount;
    existing.count += 1;
    if (tx.direction === 'Credit') existing.credits += amount;
    if (tx.direction === 'Debit') existing.debits += amount;
    map.set(label, existing);
  }

  return Array.from(map.entries())
    .map(([name, stats]) => ({
      name,
      total: Math.round(stats.total * 100) / 100,
      count: stats.count,
      direction: stats.credits > 0 && stats.debits > 0 ? 'Mixed' as const : stats.credits >= stats.debits ? 'Credit' as const : 'Debit' as const,
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, limit);
}

export function computeExtendedKpis(transactions: TransactionRow[]): ExtendedKpis {
  let largestCredit = 0;
  let largestDebit = 0;
  let feeTotal = 0;
  let totalAmount = 0;
  const dayCounts = new Map<string, number>();

  for (const tx of transactions) {
    const amount = getTransactionAmount(tx);
    totalAmount += amount;

    if (tx.type === 'Fee') feeTotal += amount;
    if (tx.direction === 'Credit' && amount > largestCredit) largestCredit = amount;
    if (tx.direction === 'Debit' && amount > largestDebit) largestDebit = amount;

    const date = parseTxDate(tx.createdAt);
    if (date) {
      const dayKey = date.toISOString().slice(0, 10);
      dayCounts.set(dayKey, (dayCounts.get(dayKey) || 0) + 1);
    }
  }

  let mostActiveDay: string | null = null;
  let maxDayCount = 0;
  dayCounts.forEach((count, key) => {
    if (count > maxDayCount) {
      maxDayCount = count;
      mostActiveDay = key;
    }
  });

  const timeSeries = buildTimeSeries(transactions);

  return {
    avgSize: transactions.length ? Math.round((totalAmount / transactions.length) * 100) / 100 : 0,
    largestCredit: Math.round(largestCredit * 100) / 100,
    largestDebit: Math.round(largestDebit * 100) / 100,
    feeTotal: Math.round(feeTotal * 100) / 100,
    mostActiveDay: mostActiveDay ? formatShortDate(mostActiveDay) : null,
    dailyNetSparkline: timeSeries.map((point) => point.net),
  };
}

export function detectInsightChips(transactions: TransactionRow[]): InsightChip[] {
  const chips: InsightChip[] = [];
  const extended = computeExtendedKpis(transactions);
  const debits = transactions.filter((tx) => tx.direction === 'Debit');

  if (extended.feeTotal > 0) {
    chips.push({
      id: 'fees',
      label: 'Fees detected',
      detail: `${transactions.filter((tx) => tx.type === 'Fee').length} fee transaction(s) · ${extended.feeTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })} total`,
      tone: 'warning',
    });
  }

  if (extended.largestDebit >= 50) {
    chips.push({
      id: 'large-debit',
      label: 'Large debit',
      detail: `Largest outgoing payment is ${extended.largestDebit.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`,
      tone: 'warning',
    });
  }

  const sortedDates = transactions
    .map((tx) => parseTxDate(tx.createdAt))
    .filter((date): date is Date => date !== null)
    .sort((a, b) => a.getTime() - b.getTime());

  if (sortedDates.length >= 2) {
    let maxGapDays = 0;
    for (let i = 1; i < sortedDates.length; i += 1) {
      const gap = (sortedDates[i].getTime() - sortedDates[i - 1].getTime()) / (1000 * 60 * 60 * 24);
      if (gap > maxGapDays) maxGapDays = gap;
    }
    if (maxGapDays >= 7) {
      chips.push({
        id: 'quiet-period',
        label: 'Quiet period',
        detail: `Longest gap between activity is ${Math.floor(maxGapDays)} days`,
        tone: 'info',
      });
    }
  }

  if (debits.length === 0 && transactions.some((tx) => tx.direction === 'Credit')) {
    chips.push({
      id: 'credits-only',
      label: 'Credits only',
      detail: 'No debits in the selected range',
      tone: 'neutral',
    });
  }

  const topType = buildTypeDonutData(transactions)[0];
  if (topType) {
    chips.push({
      id: 'top-type',
      label: 'Top type',
      detail: `${topType.name} accounts for the highest volume`,
      tone: 'info',
    });
  }

  return chips;
}

export function groupTransactionsByDate(transactions: TransactionRow[]): Array<{ dateKey: string; dateLabel: string; items: TransactionRow[] }> {
  const groups = new Map<string, TransactionRow[]>();

  for (const tx of transactions) {
    const date = parseTxDate(tx.createdAt);
    const dateKey = date ? date.toISOString().slice(0, 10) : 'unknown';
    const list = groups.get(dateKey) || [];
    list.push(tx);
    groups.set(dateKey, list);
  }

  return Array.from(groups.entries())
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([dateKey, items]) => ({
      dateKey,
      dateLabel: dateKey === 'unknown' ? 'Unknown date' : formatShortDate(dateKey),
      items,
    }));
}

export function buildTransactionExportFilename(options: {
  artistName?: string | null;
  accountId?: string;
}): string {
  const rawName = options.artistName?.trim() || options.accountId?.trim() || 'account';
  const safeName = rawName
    .replace(/[/\\?%*:|"<>]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);

  return `${safeName || 'account'}-transactions.csv`;
}

export function exportTransactionsCsv(transactions: TransactionRow[], filename = 'transactions.csv'): void {
  const headers = ['ID', 'Summary', 'Date', 'Type', 'Direction', 'Amount', 'Counterparty'];
  const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;

  const rows = transactions.map((tx) => [
    tx.id != null ? String(tx.id) : '',
    tx.summary || '',
    tx.createdAt || '',
    tx.type || '',
    tx.direction || '',
    String(getTransactionAmount(tx)),
    tx.name || '',
  ]);

  const csv = [headers, ...rows].map((row) => row.map((cell) => escape(cell)).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function computePercentChange(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10;
}

type SortField = 'date' | 'amount' | 'type' | 'summary';
type SortDir = 'asc' | 'desc';

export function sortTransactions(
  transactions: TransactionRow[],
  field: SortField,
  direction: SortDir
): TransactionRow[] {
  const sorted = [...transactions];
  const factor = direction === 'asc' ? 1 : -1;

  sorted.sort((a, b) => {
    if (field === 'amount') {
      return (getTransactionAmount(a) - getTransactionAmount(b)) * factor;
    }
    if (field === 'date') {
      const aTime = parseTxDate(a.createdAt)?.getTime() ?? 0;
      const bTime = parseTxDate(b.createdAt)?.getTime() ?? 0;
      return (aTime - bTime) * factor;
    }
    if (field === 'type') {
      return (a.type || '').localeCompare(b.type || '') * factor;
    }
    return (a.summary || '').localeCompare(b.summary || '') * factor;
  });

  return sorted;
}

export type { SortField, SortDir };
